// ============================================
// FILE: src/components/HomeScreen.jsx
// ============================================

import React, { useState, useEffect } from 'react';
import { Search, Bell, MapPin, Heart, Gift, Package, Plus, ShoppingCart, LogOut, Flame, ChevronRight } from 'lucide-react';

import LocationModal from './modals/LocationModal';
import ProductDetailsModal from './modals/ProductDetailsModal';
import { banners, locations, subscriptionPlans, categoryIcons } from '../data/products';
import { getProductImage, getProductEmoji } from '../utils/imageHelpers';

const HomeScreen = ({
  searchQuery = '',
  setSearchQuery,
  setShowNotifications,
  setShowLocationModal,
  showLocationModal,
  selectedLocation,
  setSelectedLocation,
  currentBanner,
  setCurrentBanner,
  addToCart,
  setShowSubscription,
  products,
  filteredProducts,
  cart = [],
  getCartCount,
  getCartTotal,
  updateQuantity,
  openCheckout,
  userProfile = {},
  notifications = [],
  favorites = [],
  setShowFavorites,
  setFavorites,
  toggleFavorite,
  setActiveTab,
  selectedCategory = 'All',
  setSelectedCategory,
  categories: apiCategories = [],
  showToast,
  appSettings,
  handleLogout,
  featureSections = [],
  appTheme = 'blue',
}) => {
  const favArray = Array.isArray(favorites) ? favorites : [];
  const logoSrc = `${process.env.PUBLIC_URL || ''}/logo.png`;
  const isProductFavorited = (productId) => (
    favArray.some((item) => String(item?.id) === String(productId))
  );
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductDetails, setShowProductDetails] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [quantitySelections, setQuantitySelections] = useState({});
  const [quantityLoading, setQuantityLoading] = useState({});
  const productsPerPage = 40;

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % banners.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [setCurrentBanner]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory]);

  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);
  const startIndex = (currentPage - 1) * productsPerPage;
  const endIndex = startIndex + productsPerPage;
  const paginatedProducts = filteredProducts.slice(startIndex, endIndex);

  const categories = apiCategories.length > 0
    ? ['All', ...apiCategories.map(c => c.name)]
    : ['All', ...new Set(products.map(p =>
        typeof p.category === 'object' && p.category?.name ? p.category.name : p.category || 'Uncategorized'
      ).filter(Boolean))];

  const getWeightParts = (product) => {
    if (!product) return { value: null, unit: '' };
    if (product.weight !== undefined && product.weight !== null && product.weight !== '') {
      const raw = String(product.weight).trim();
      const match = raw.match(/^(\d*\.?\d+)\s*([a-zA-Z]*)$/);
      if (match) return { value: parseFloat(match[1]), unit: match[2] || product.unit || '' };
    }
    return { value: null, unit: product.unit || '' };
  };

  const isWeightUnit = (unit) => {
    if (!unit) return false;
    return ['kg', 'gram', 'g'].includes(String(unit).toLowerCase().trim());
  };

  const formatUnit = (unit) => {
    if (!unit) return '';
    const str = String(unit).trim();
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const getQuantityOptions = (product) => {
    const { unit } = getWeightParts(product);
    return isWeightUnit(unit) ? [0.5, 1, 2, 3] : [1, 2, 3, 4];
  };

  const formatWeightLabel = (product) => {
    const { value, unit } = getWeightParts(product);
    if (value) return `${value}${unit ? ` ${unit}` : ''}`;
    return product.weight ? String(product.weight) : '';
  };

  const getSelectedQuantity = (productId) => quantitySelections[productId] || 1;

  const setSelectedQuantity = (productId, qty) => {
    setQuantitySelections((prev) => ({ ...prev, [productId]: qty }));
  };

  const handleAddToCartWithQuantity = (product) => {
    addToCart(product, getSelectedQuantity(product.id));
  };

  return (
    <div className="pb-20" style={{ background: '#f7f7f7', color: '#1b1b1b', minHeight: '100vh' }}>

      {/* ── UC-style sticky header ── */}
      <div className="sticky top-0 z-30 bg-white" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>

        {/* Top bar */}
        <div className="flex items-center justify-between px-4 pt-3 pb-2" style={{ borderBottom: '1px solid #f0f0f0' }}>
          <button onClick={() => setShowLocationModal(true)} className="flex items-center gap-1.5 min-w-0">
            <MapPin size={16} style={{ color: '#f05a23', flexShrink: 0 }} />
            <span className="text-sm font-semibold text-gray-800 truncate max-w-[160px]">{selectedLocation}</span>
            <span className="text-gray-400 text-xs">▾</span>
          </button>

          <div className="flex items-center gap-1 flex-shrink-0">
            <button onClick={() => setShowNotifications(true)} className="relative p-2 rounded-full hover:bg-gray-100 transition-all">
              <Bell size={20} className="text-gray-700" />
              {notifications.filter(n => n.unread).length > 0 && (
                <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-white flex items-center justify-center">
                  <span className="text-[9px] font-bold text-white">{notifications.filter(n => n.unread).length}</span>
                </span>
              )}
            </button>
            <button onClick={() => setShowFavorites(true)} className="relative p-2 rounded-full hover:bg-gray-100 transition-all">
              <Heart size={20} className="text-gray-700" />
              {favArray.length > 0 && (
                <span className="absolute top-1 right-1 w-4 h-4 bg-pink-500 rounded-full border-2 border-white flex items-center justify-center">
                  <span className="text-[9px] font-bold text-white">{favArray.length}</span>
                </span>
              )}
            </button>
            {handleLogout && (
              <button onClick={handleLogout} className="p-2 rounded-full hover:bg-gray-100 transition-all" title="Logout">
                <LogOut size={20} className="text-gray-700" />
              </button>
            )}
          </div>
        </div>

        {/* Brand + search */}
        <div className="px-4 pt-3 pb-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm" style={{ background: '#f3f4f6' }}>
              <img src={logoSrc} alt={appSettings?.appName || 'logo'} className="w-8 h-8 rounded-xl object-contain" />
            </div>
            <div className="min-w-0">
              <h1 className="app-brand-title truncate" style={{ color: '#1b1b1b' }}>
                {appSettings?.appName || 'DealStore'}
              </h1>
              {userProfile?.name
                ? <p className="text-xs text-gray-500">Hey <span className="font-semibold text-gray-700">{userProfile.name}</span> 👋</p>
                : <p className="text-xs text-gray-500">Fresh picks &amp; daily savings</p>
              }
            </div>
          </div>

          <div className="flex items-center gap-3 px-4 py-3 rounded-2xl" style={{ background: '#f2f2f2', border: '1.5px solid #e8e8e8' }}>
            <Search size={18} className="text-gray-400 flex-shrink-0" />
            <input
              type="text"
              placeholder="Search for products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 bg-transparent outline-none text-sm text-gray-800 placeholder-gray-400"
            />
            {searchQuery.length > 0 && (
              <button onClick={() => setSearchQuery('')} className="text-gray-400 text-xs flex-shrink-0">✕</button>
            )}
          </div>
        </div>
      </div>

      {/* Quick action pills */}
      <div className="flex gap-2 px-4 py-3 overflow-x-auto hide-scrollbar bg-white" style={{ borderBottom: '1px solid #f0f0f0' }}>
        {[
          { icon: <Gift size={14} />, label: 'Offers', action: () => document.querySelector('.banner-carousel')?.scrollIntoView({ behavior: 'smooth' }) },
          { icon: <Package size={14} />, label: 'Track Order', action: () => setActiveTab && setActiveTab('orders') },
          { icon: <span className="text-xs">🔔</span>, label: 'Subscribe', action: () => setShowSubscription((filteredProducts[0] || products[0]) ?? null) },
        ].map(({ icon, label, action }) => (
          <button key={label} onClick={action}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium text-gray-700 transition-all hover:bg-orange-50"
            style={{ background: '#fff', border: '1.5px solid #e8e8e8' }}
          >
            <span style={{ color: '#f05a23' }}>{icon}</span>
            {label}
          </button>
        ))}
      </div>

      {/* Banner Carousel */}
      <div className="px-4 mt-4 mb-6 banner-carousel">
        <div className="relative overflow-hidden rounded-2xl shadow-lg">
          <div
            className="flex banner-slide"
            style={{ transform: `translateX(-${currentBanner * 100}%)`, transition: 'transform 0.5s ease-in-out' }}
          >
            {banners.map((banner) => (
              <div
                key={banner.id}
                className="min-w-full relative overflow-hidden flex items-center"
                style={{ background: banner.bgStyle || `linear-gradient(135deg,#3b82f6,#2563eb)`, minHeight: 180 }}
              >
                <div className="absolute -top-10 -right-10 w-44 h-44 rounded-full bg-white/10 pointer-events-none" />
                <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full bg-white/10 pointer-events-none" />

                <div className="flex-1 px-5 py-5 z-10" style={{ maxWidth: '55%' }}>
                  <span className="bg-white/25 backdrop-blur-sm text-white text-[10px] font-bold px-3 py-1 rounded-full inline-block mb-2 tracking-wide uppercase">
                    {banner.offer}
                  </span>
                  <h2 className="text-xl font-extrabold text-white leading-tight mb-1">{banner.title}</h2>
                  <p className="text-white/75 text-xs mb-4 leading-relaxed">{banner.subtitle}</p>
                  <button
                    onClick={() => {
                      if (banner.images) setSelectedCategory && setSelectedCategory('Fruits');
                      setTimeout(() => document.querySelector('.products-section')?.scrollIntoView({ behavior: 'smooth' }), 50);
                    }}
                    className="bg-white text-xs font-bold px-4 py-2 rounded-xl shadow-lg hover:scale-105 active:scale-95 transition-transform"
                    style={{ color: '#f05a23' }}
                  >
                    Shop Now →
                  </button>
                </div>

                <div className="absolute right-0 top-0 bottom-0 z-10" style={{ width: '48%' }}>
                  {banner.images ? (
                    <>
                      <img src={`${process.env.PUBLIC_URL}${banner.images[0]}`} alt=""
                        className="absolute object-contain" style={{ width: 72, height: 72, right: 8, top: 6, transform: 'rotate(10deg)', filter: 'drop-shadow(0 6px 10px rgba(0,0,0,0.35))' }} />
                      <img src={`${process.env.PUBLIC_URL}${banner.images[1]}`} alt=""
                        className="absolute object-contain" style={{ width: 64, height: 64, right: 52, top: 18, transform: 'rotate(-6deg)', filter: 'drop-shadow(0 6px 10px rgba(0,0,0,0.3))' }} />
                      <img src={`${process.env.PUBLIC_URL}${banner.images[2]}`} alt=""
                        className="absolute object-contain" style={{ width: 60, height: 60, right: 14, bottom: 10, transform: 'rotate(8deg)', filter: 'drop-shadow(0 6px 10px rgba(0,0,0,0.3))' }} />
                      <img src={`${process.env.PUBLIC_URL}${banner.images[3]}`} alt=""
                        className="absolute object-contain" style={{ width: 46, height: 46, right: 60, bottom: 8, transform: 'rotate(-12deg)', filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.25))' }} />
                      <img src={`${process.env.PUBLIC_URL}${banner.images[4]}`} alt=""
                        className="absolute object-contain" style={{ width: 40, height: 40, right: 2, top: '42%', transform: 'rotate(-4deg) translateY(-50%)', filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.25))' }} />
                    </>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <span style={{ fontSize: 90, lineHeight: 1, filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.25))' }}>
                        {banner.icon}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex gap-2">
            {banners.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentBanner(index)}
                className={`h-2 rounded-full transition-all ${currentBanner === index ? 'bg-white w-6' : 'bg-white/50 w-2'}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* ── Fresh Fruit Deals ── */}
      <div className="px-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-bold text-gray-800">🛒 Fresh Fruit Deals</h2>
          <span className="text-xs font-semibold text-orange-500 bg-orange-50 px-2 py-0.5 rounded-full border border-orange-200">Limited Time</span>
        </div>
        <div className="grid grid-cols-2 gap-3">

          {/* Imported Fruits */}
          <div
            className="relative rounded-2xl overflow-hidden cursor-pointer active:scale-[0.98] transition-transform"
            style={{ background: '#fff', minHeight: 160, border: '1.5px solid #fed7aa', boxShadow: '0 2px 12px rgba(234,88,12,0.10)' }}
            onClick={() => {
              setSelectedCategory && setSelectedCategory('Fruits');
              setTimeout(() => document.querySelector('.products-section')?.scrollIntoView({ behavior: 'smooth' }), 50);
            }}
          >
            <div className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl" style={{ background: 'linear-gradient(90deg, #f59e0b, #ea580c)' }} />
            <div className="flex flex-col justify-between h-full p-3 pt-4">
              <div>
                <span className="text-[9px] font-bold text-orange-600 bg-orange-100 px-2 py-0.5 rounded-full">Up to 30% Off</span>
                <h3 className="text-gray-800 font-bold text-sm leading-tight mt-2">Exotic<br />Imported<br />Fruits</h3>
                <p className="text-gray-400 text-[9px] mt-1">Pineapple · Grapes · Mango</p>
              </div>
              <span className="text-[9px] font-semibold text-orange-500 mt-2 inline-block">Shop Now →</span>
            </div>
            <div className="absolute right-0 bottom-0 z-10" style={{ width: '52%', height: '100%' }}>
              <img
                src={`${process.env.PUBLIC_URL}/uploads/categories/basket2.jpeg`}
                alt="Imported fruits basket"
                className="absolute object-cover rounded-br-2xl"
                style={{ right: 0, bottom: 0, width: '100%', height: '100%', objectPosition: 'center' }}
              />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, #fff 0%, transparent 40%)' }} />
            </div>
          </div>

          {/* Season Fruits */}
          <div
            className="relative rounded-2xl overflow-hidden cursor-pointer active:scale-[0.98] transition-transform"
            style={{ background: '#fff', minHeight: 160, border: '1.5px solid #fecdd3', boxShadow: '0 2px 12px rgba(244,63,94,0.10)' }}
            onClick={() => {
              setSelectedCategory && setSelectedCategory('Fruits');
              setTimeout(() => document.querySelector('.products-section')?.scrollIntoView({ behavior: 'smooth' }), 50);
            }}
          >
            <div className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl" style={{ background: 'linear-gradient(90deg, #f43f5e, #be185d)' }} />
            <div className="flex flex-col justify-between h-full p-3 pt-4">
              <div>
                <span className="text-[9px] font-bold text-rose-600 bg-rose-100 px-2 py-0.5 rounded-full">Season Fresh</span>
                <h3 className="text-gray-800 font-bold text-sm leading-tight mt-2">Season<br />Fruits<br />Deals</h3>
                <p className="text-gray-400 text-[9px] mt-1">Mango · Watermelon</p>
              </div>
              <span className="text-[9px] font-semibold text-rose-500 mt-2 inline-block">Shop Now →</span>
            </div>
            <div className="absolute right-0 bottom-0 z-10" style={{ width: '52%', height: '100%' }}>
              <img
                src={`${process.env.PUBLIC_URL}/uploads/categories/basket1.jpeg`}
                alt="Season fruits basket"
                className="absolute object-cover rounded-br-2xl"
                style={{ right: 0, bottom: 0, width: '100%', height: '100%', objectPosition: 'center' }}
              />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, #fff 0%, transparent 40%)' }} />
            </div>
          </div>

        </div>
      </div>

      {/* Feature Sections */}
      {Array.isArray(featureSections) && featureSections.map(section => {
        const sectionColor = section.color || '#3b82f6';
        return (
          <div key={section.id} className="mb-7">
            <div className="mx-4 mb-3 rounded-2xl overflow-hidden shadow-sm">
              <div
                className="flex items-center justify-between px-4 py-3"
                style={{ background: `linear-gradient(135deg, ${sectionColor}22 0%, ${sectionColor}08 100%)`, borderLeft: `4px solid ${sectionColor}` }}
              >
                <div className="flex items-center gap-2.5">
                  <div className="flex items-center justify-center w-9 h-9 rounded-xl text-white shadow-md"
                    style={{ background: `linear-gradient(135deg, ${sectionColor}, ${sectionColor}99)` }}>
                    {section.icon ? <i className={`${section.icon} text-sm`} /> : <Flame size={16} />}
                  </div>
                  <div>
                    <h2 className="text-sm font-bold text-gray-900 leading-tight">{section.name}</h2>
                    <p className="text-[10px] text-gray-400">{(section.products || []).length} items available</p>
                  </div>
                  <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold text-white ml-1"
                    style={{ backgroundColor: sectionColor }}>
                    <span className="w-1.5 h-1.5 rounded-full bg-white/80 motion-safe:animate-pulse inline-block" />
                    LIVE
                  </span>
                </div>
                <button className="flex items-center gap-0.5 text-xs font-semibold" style={{ color: sectionColor }}>
                  All <ChevronRight size={14} />
                </button>
              </div>
            </div>

            <div className="flex gap-3 overflow-x-auto px-4 pb-3 hide-scrollbar">
              {(section.products || []).map(product => {
                const isFav = isProductFavorited(product.id);
                const hasDiscount = Number(product.sale_price) > 0 && Number(product.price) > 0 && Number(product.sale_price) < Number(product.price);
                const offPercent = hasDiscount
                  ? Math.round(((Number(product.price) - Number(product.sale_price)) / Number(product.price)) * 100)
                  : 0;
                return (
                  <div key={product.id} className="min-w-[148px] max-w-[148px] rounded-2xl overflow-hidden flex-shrink-0 bg-white shadow-md"
                    style={{ borderTop: `3px solid ${sectionColor}` }}>
                    <div className="relative h-32 overflow-hidden cursor-pointer group"
                      onClick={() => { setSelectedProduct(product); setShowProductDetails(true); }}>
                      {getProductImage(product, 'card')}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent pointer-events-none" />
                      {hasDiscount && (
                        <span className="absolute top-2 left-2 text-[10px] font-bold text-white px-2 py-0.5 rounded-full shadow-md"
                          style={{ background: 'linear-gradient(90deg,#f59e0b,#ef4444)' }}>
                          {offPercent}% OFF
                        </span>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); if (typeof toggleFavorite === 'function') toggleFavorite(product); }}
                        className="absolute top-2 right-2 p-1.5 bg-white/90 backdrop-blur-sm rounded-full shadow hover:scale-110 transition-transform"
                      >
                        <Heart size={12} className={isFav ? 'text-pink-500' : 'text-gray-400'} fill={isFav ? 'currentColor' : 'none'} />
                      </button>
                    </div>
                    <div className="p-2.5">
                      <h3 className="text-xs font-semibold text-gray-800 mb-1.5 line-clamp-2 cursor-pointer hover:text-orange-500 leading-tight"
                        onClick={() => { setSelectedProduct(product); setShowProductDetails(true); }}>
                        {product.name}
                      </h3>
                      <div className="flex items-center gap-1.5 mb-2">
                        {hasDiscount ? (
                          <>
                            <span className="text-sm font-bold text-gray-900">₹{product.sale_price}</span>
                            <span className="text-[10px] text-gray-400 line-through">₹{product.price}</span>
                          </>
                        ) : (
                          <span className="text-sm font-bold text-gray-900">₹{product.price}</span>
                        )}
                      </div>
                      <button
                        onClick={() => addToCart(product, 1)}
                        className="w-full text-[11px] text-white py-1.5 rounded-xl font-bold transition-all hover:opacity-90 active:scale-95 flex items-center justify-center gap-1"
                        style={{ background: `linear-gradient(90deg, ${sectionColor}, ${sectionColor}cc)` }}
                      >
                        <Plus size={11} /> Add
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* ── UC-style Category Bar ── */}
      <div className="bg-white mb-3" style={{ borderBottom: '1px solid #f0f0f0' }}>
        <div className="flex items-center justify-between px-4 pt-3 pb-1">
          <h2 className="text-sm font-bold text-gray-800">Shop by Category</h2>
          <span className="text-xs text-gray-400">{categories.length - 1} types</span>
        </div>
        <div className="flex gap-3 overflow-x-auto px-4 py-3 hide-scrollbar">
          {categories.map(category => {
            const isActive = selectedCategory === category;
            const catPalettes = ['#FF6B6B','#4ECDC4','#45B7D1','#96CEB4','#FFEAA7','#DDA0DD','#98D8C8','#F7DC6F','#BB8FCE','#85C1E9'];
            const idx = categories.indexOf(category);
            const circleColor = isActive ? '#f05a23' : catPalettes[idx % catPalettes.length];
            return (
              <button
                key={category}
                onClick={() => setSelectedCategory && setSelectedCategory(category)}
                className="flex flex-col items-center gap-1.5 flex-shrink-0 transition-all"
                style={{ minWidth: 64 }}
              >
                <div className="w-14 h-14 rounded-full flex items-center justify-center shadow-sm transition-all"
                  style={{
                    background: isActive ? 'linear-gradient(135deg,#f05a23,#f7821a)' : `${circleColor}22`,
                    border: isActive ? '2.5px solid #f05a23' : `2px solid ${circleColor}55`,
                    transform: isActive ? 'scale(1.08)' : 'scale(1)',
                  }}>
                  {category === 'All'
                    ? <ShoppingCart size={22} style={{ color: isActive ? '#fff' : circleColor }} strokeWidth={2} />
                    : <span className="text-2xl leading-none">{categoryIcons[category] || '📦'}</span>
                  }
                </div>
                <span className="text-[11px] font-semibold text-center leading-tight"
                  style={{ color: isActive ? '#f05a23' : '#555', maxWidth: 64 }}>
                  {category}
                </span>
                {isActive && <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />}
              </button>
            );
          })}
        </div>
      </div>

      {/* Products Grid */}
      <div className="p-4 products-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-800 font-display">
            {selectedCategory === 'All' ? 'Our Products' : selectedCategory}
          </h2>
          <span className="text-sm text-gray-500">
            {filteredProducts.length} {filteredProducts.length === 1 ? 'product' : 'products'}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {paginatedProducts.map((product, index) => {
            const isFav = isProductFavorited(product.id);
            const hasDiscount = Number(product.sale_price) > 0 && Number(product.price) > 0 && Number(product.sale_price) < Number(product.price);
            const offPercent = hasDiscount
              ? Math.round(((Number(product.price) - Number(product.sale_price)) / Number(product.price)) * 100)
              : 0;
            return (
              <div key={product.id} className="rounded-2xl overflow-hidden relative product-card glass-card"
                style={{ animationDelay: `${(index % 10) * 50}ms` }}>
                <div
                  className="relative overflow-hidden h-44 cursor-pointer glass-image group"
                  onClick={() => { setSelectedProduct(product); setShowProductDetails(true); }}
                >
                  {getProductImage(product, 'card')}
                  {hasDiscount && (
                    <span className="absolute top-3 left-3 text-xs bg-gradient-to-r from-amber-500 via-orange-500 to-pink-600 text-white px-2 py-0.5 rounded-full font-semibold shadow-sm ring-1 ring-white/70 badge-off">
                      {offPercent}% OFF
                    </span>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); if (typeof toggleFavorite === 'function') toggleFavorite(product); }}
                    className="absolute top-3 right-3 p-2 bg-white rounded-full shadow hover:scale-105 transition-transform"
                  >
                    <Heart size={18} className={isFav ? 'text-pink-500' : 'text-gray-400'} fill={isFav ? 'currentColor' : 'none'} />
                  </button>
                </div>

                <div className="p-3">
                  <h3
                    className="font-display font-semibold text-gray-800 text-sm mb-1 cursor-pointer hover:text-orange-500 transition-colors"
                    onClick={() => { setSelectedProduct(product); setShowProductDetails(true); }}
                  >{product.name}</h3>
                  <div className="flex items-center gap-2 mb-2">
                    {product.weight && (
                      <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">{formatWeightLabel(product)}</span>
                    )}
                    {!product.weight && product.fat && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">{product.fat} fat</span>
                    )}
                    {!product.weight && !product.fat && product.brand && (
                      <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">{product.brand}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    {product.sale_price && product.sale_price < product.price ? (
                      <>
                        <span className="text-sm font-extrabold text-green-600 drop-shadow-sm">₹{product.sale_price}</span>
                        <span className="text-xs text-gray-400 line-through">₹{product.price}</span>
                      </>
                    ) : (
                      <span className="text-sm font-extrabold text-emerald-600 drop-shadow-sm">₹{product.price}</span>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      {getQuantityOptions(product).map((qty) => (
                        <button
                          key={qty}
                          type="button"
                          onClick={() => setSelectedQuantity(product.id, qty)}
                          className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                            getSelectedQuantity(product.id) === qty
                              ? 'bg-orange-500 text-white border-orange-500'
                              : 'bg-white text-gray-700 border-gray-200 hover:border-orange-300'
                          }`}
                        >
                          {qty}
                        </button>
                      ))}
                      {isWeightUnit(getWeightParts(product).unit) && (
                        <span className="text-xs text-gray-600 ml-1 font-medium">Kg</span>
                      )}
                      {!isWeightUnit(getWeightParts(product).unit) && getWeightParts(product).unit && (
                        <span className="text-xs text-gray-600 ml-1 font-medium">{formatUnit(getWeightParts(product).unit)}</span>
                      )}
                    </div>

                    {(() => {
                      const cartItem = cart.find(item => item.id === product.id);
                      if (cartItem && cartItem.quantity > 0) {
                        return (
                          <div className="w-full flex items-center justify-center gap-2 bg-orange-50 border border-orange-300 rounded-lg px-2 py-1 mt-1">
                            <button
                              className="flex items-center justify-center w-8 h-8 rounded-full bg-white border border-orange-300 text-orange-600 text-xl font-bold shadow-sm hover:bg-orange-50 focus:outline-none disabled:opacity-50"
                              disabled={quantityLoading[product.id] || cartItem.quantity <= 1}
                              onClick={async () => {
                                if (typeof updateQuantity === 'function' && cartItem.quantity > 1) {
                                  setQuantityLoading(prev => ({ ...prev, [product.id]: true }));
                                  await updateQuantity(product.id, -1);
                                  setQuantityLoading(prev => ({ ...prev, [product.id]: false }));
                                }
                              }}
                            >
                              <span>&#8722;</span>
                            </button>
                            <span className="text-base font-bold text-orange-600 w-10 text-center select-none">
                              {quantityLoading[product.id] ? '...' : cartItem.quantity}
                            </span>
                            <button
                              className="flex items-center justify-center w-8 h-8 rounded-full bg-white border border-orange-300 text-orange-600 text-xl font-bold shadow-sm hover:bg-orange-50 focus:outline-none disabled:opacity-50"
                              disabled={!!quantityLoading[product.id]}
                              onClick={async () => {
                                if (typeof updateQuantity === 'function' && !quantityLoading[product.id]) {
                                  setQuantityLoading(prev => ({ ...prev, [product.id]: true }));
                                  await updateQuantity(product.id, 1);
                                  setQuantityLoading(prev => ({ ...prev, [product.id]: false }));
                                }
                              }}
                            >
                              <span>&#43;</span>
                            </button>
                          </div>
                        );
                      }
                      return (
                        <button
                          onClick={() => handleAddToCartWithQuantity(product)}
                          className="w-full bg-orange-500 text-white py-2 rounded-lg text-sm font-medium hover:bg-orange-600 transition-colors flex items-center justify-center gap-1"
                        >
                          <Plus size={16} />
                          Add to Cart
                        </button>
                      );
                    })()}
                  </div>

                  {Boolean(Number(product.is_subscribable)) && (
                    <button
                      onClick={() => setShowSubscription(product)}
                      className="w-full bg-green-500 text-white py-2 rounded-lg text-sm font-medium hover:bg-green-600 transition-colors mt-2"
                    >
                      Subscribe &amp; Save
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6 mb-4">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${currentPage === 1 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-orange-500 text-white hover:bg-orange-600'}`}
            >
              Previous
            </button>
            <div className="flex gap-2">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`w-10 h-10 rounded-lg font-medium transition-all ${currentPage === page ? 'bg-orange-500 text-white shadow-lg' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
                >
                  {page}
                </button>
              ))}
            </div>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${currentPage === totalPages ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-orange-500 text-white hover:bg-orange-600'}`}
            >
              Next
            </button>
          </div>
        )}
        {totalPages > 1 && (
          <p className="text-center text-sm text-gray-500 mb-4">
            Showing {startIndex + 1}–{Math.min(endIndex, filteredProducts.length)} of {filteredProducts.length} products
          </p>
        )}
      </div>

      {/* Floating Checkout */}
      {cart.length > 0 && getCartCount() > 0 && (
        <button
          onClick={openCheckout}
          className="fixed bottom-24 right-6 bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-4 rounded-full shadow-2xl hover:shadow-green-500/50 transition-all transform hover:scale-105 flex items-center gap-3 z-50"
        >
          <ShoppingCart size={24} />
          <div className="flex flex-col items-start">
            <span className="text-xs font-medium">Checkout Now</span>
            <span className="text-lg font-bold">{getCartCount()} {getCartCount() === 1 ? 'item' : 'items'}</span>
            <span className="text-xs font-semibold mt-1 text-green-100 bg-green-700 bg-opacity-70 px-2 py-0.5 rounded">
              Total: ₹{typeof getCartTotal === 'function' ? getCartTotal() : cart.reduce((sum, item) => {
                const p = (item.sale_price && item.sale_price < item.price) ? item.sale_price : item.price;
                return sum + (p * item.quantity);
              }, 0)}
            </span>
          </div>
        </button>
      )}

      {/* Product Details Modal */}
      <ProductDetailsModal
        isOpen={showProductDetails}
        onClose={() => { setShowProductDetails(false); setSelectedProduct(null); }}
        product={selectedProduct}
        addToCart={addToCart}
        showToast={showToast}
        favorites={favorites}
        toggleFavorite={toggleFavorite}
        setShowSubscription={setShowSubscription}
        appTheme={appTheme}
      />
    </div>
  );
};

export default HomeScreen;
